// components/OptionEditor.tsx
import React from 'react';
import { Stack, Group, TextInput, ActionIcon, Text, Button } from '@mantine/core';
import { IconArrowUp, IconArrowDown, IconTrash, IconPlus, IconGripVertical } from '@tabler/icons-react';
import { FieldOption } from '../types/BuilderField';

export interface OptionEditorProps {
  options: FieldOption[];
  onChange: (options: FieldOption[]) => void;
}

export function OptionEditor({ options, onChange }: OptionEditorProps) {
  const update = (index: number, patch: Partial<FieldOption>) => {
    onChange(options.map((o, i) => (i === index ? { ...o, ...patch } : o)));
  };

  const move = (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= options.length) return;
    const next = [...options];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  const remove = (index: number) => onChange(options.filter((_, i) => i !== index));
  const add = () => onChange([...options, { label: '', value: '' }]);

  return (
    <Stack gap="xs">
      <Text size="sm" fw={500}>Options</Text>
      {options.map((opt, i) => (
        <Group key={i} gap="xs" wrap="nowrap">
          <IconGripVertical size={14} style={{ opacity: 0.4 }} />
          <TextInput
            placeholder="Label"
            value={opt.label}
            onChange={(e) => update(i, { label: e.currentTarget.value })}
            style={{ flex: 1 }}
          />
          <TextInput
            placeholder="Value"
            value={opt.value}
            onChange={(e) => update(i, { value: e.currentTarget.value })}
            style={{ flex: 1 }}
          />
          <ActionIcon variant="subtle" onClick={() => move(i, -1)} disabled={i === 0}>
            <IconArrowUp size={14} />
          </ActionIcon>
          <ActionIcon variant="subtle" onClick={() => move(i, 1)} disabled={i === options.length - 1}>
            <IconArrowDown size={14} />
          </ActionIcon>
          <ActionIcon color="red" variant="subtle" onClick={() => remove(i)}>
            <IconTrash size={14} />
          </ActionIcon>
        </Group>
      ))}
      <Button variant="light" size="xs" leftSection={<IconPlus size={14} />} onClick={add}>
        Add Option
      </Button>
    </Stack>
  );
}